// Scope key sets — used by switcher functions to clear the right state
// entries on each scope boundary.

import {
  STATE_DEFAULTS as stateDefaults,
  WORKSPACE_SCOPED_KEYS,
  WORKSPACE_SCOPED_OVERRIDES
} from './state.js'
import { moduleScopedWipe } from './functions/moduleScopedKeys.js'

// USER scope — cleared only on logout
export const USER_KEYS = new Set(['user', 'preferences', 'themeMode', 'lang'])

// ORG scope — cleared on switchOrg (cascades to all below)
export const ORG_KEYS = new Set(['org', 'permissions', 'orgMembers', 'orgRoles', 'orgTeams'])

// WORKSPACE scope — cleared on switchWorkspace
export const WORKSPACE_KEYS = new Set([
  'workspace',
  'workspaceMembers',
  'workspaceIntegrations',
  'workspaceBilling'
])

// PROJECT scope — canvas-internal, cleared on switchProject
export const PROJECT_KEYS = new Set([
  'project',
  'projectComponents',
  'projectPages',
  'projectState'
])

// WORKSPACE PROJECT scope — cleared on switchWorkspaceProject
export const WORKSPACE_PROJECT_KEYS = new Set(['workspaceProject'])

// Fresh, type-directed empty value for a schema default. Arrays/objects
// must be NEW instances every call — `stateDefaults` is a module-level
// singleton (state.js's default export, imported once), so reusing its `[]`
// / `{}` literal directly would let one workspace's mutations bleed into
// the next switch's "empty" value. Scalars (null/false/''/numbers) are
// immutable in JS and pass through unchanged — nothing to clone.
const _freshEmpty = (v) => (Array.isArray(v) ? [] : v && typeof v === 'object' ? {} : v)

// Canonical org/workspace-switch WIPE — the SINGLE SOURCE OF TRUTH for state
// that must reset to its empty schema value on a tenant boundary
// (onWorkspaceChange's PHASE-1 `root.replace`). Driven by the
// WORKSPACE_SCOPED_KEYS registry declared next to the state defaults
// themselves (state-app.js for the WORKSPACE-PROJECT tier, state.js for the
// ORG/WORKSPACE/PROJECT tiers) — add a new org/workspace/project-scoped
// state key to THAT registry, never inline here or in onWorkspaceChange, so
// an un-wiped scoped key can no longer silently LEAK the previous org's
// data across the switch (the growing-hand-list hazard this registry
// retires; tickets/workspace.md). Returns a FRESH object every call so the
// caller's spread never shares mutable `[]` / `{}` instances between
// switches. EXCLUDES `workspace` + `__fetchScope` (onWorkspaceChange sets
// those to specific values, not empties) and UI-only filter inputs
// (searchQuery/filter*, which intentionally carry across a switch via the
// caller's `...root.parse()`) — neither is in the registry.
//
// MODULE KEYS (WS-MODULE-SCOPED-KEYS-CONTRACT-1): a workspace module declares
// its tenant-scoped root keys in its manifest (`scopedKeys`, see
// functions/moduleScopedKeys.js); the loader registers them on inject and
// this wipe spreads their typed empties too, so a module's rows reset at the
// SAME synchronous PHASE-1 beat as the shell's — no module code, no hand-wipe.
// `root` is optional: a `prefix*` family needs the live key list, the shell
// half never does. A module entry never shadows a shell registry key — the
// shell patch is written LAST.
export const workspaceScopedWipe = (root) => {
  const patch = moduleScopedWipe(root)
  for (const key of WORKSPACE_SCOPED_KEYS) {
    patch[key] = Object.prototype.hasOwnProperty.call(WORKSPACE_SCOPED_OVERRIDES, key)
      ? _freshEmpty(WORKSPACE_SCOPED_OVERRIDES[key])
      : _freshEmpty(stateDefaults[key])
  }
  return patch
}
